import DialogComp from 'components/Dialog';
import ToolTipComponent from 'components/ToolTip';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Textarea } from 'components/ui/textarea';
import { AlertCircle, PlusIcon, TrashIcon } from 'lucide-react';
import React from 'react';
import { deleteGlobalSettings, getGlobalSettings, putGlobalSettings } from 'utils/fetchers';
import { tryParseJSONObject } from 'utils/index';
import AccessRulesList from '../OrgView/AccessRulesList';

const GlobalView = () => {
  const [globalSettings, setGlobalSettings] = React.useState<Array<{
    application: string;
    settings: Record<string, any>;
  }> | null>(null);

  // Update settings when changes are made
  const getSettings = () => {
    getGlobalSettings().then(res => {
      if (res) {
        setGlobalSettings(res);
      }
    });
  };

  const availableApplications = globalSettings ? globalSettings?.map(set => set.application) : [];
  const [selectedApplication, setselectedApplication] = React.useState<string>('');

  const selectApplication = (app: string) => {
    setChangedKeys({});
    setselectedApplication(app);
  };

  const [newApplicationData, setnewApplicationData] = React.useState<{ name: string; key: string; value: any } | null>(
    null,
  );
  const onChangeNewAppData = (field: string, value: string) => {
    if (newApplicationData) {
      setnewApplicationData({ ...newApplicationData, [field]: value });
    } else {
      setnewApplicationData({ name: '', key: '', value: '', [field]: value });
    }
  };
  const [newKeyDataPair, setNewKeyDataPair] = React.useState<{ name: string; data: string } | null>(null);
  const onChangeNewKeyDataPair = (field: string, value: string) => {
    if (newKeyDataPair) {
      setNewKeyDataPair({ ...newKeyDataPair, [field]: value });
    } else {
      setNewKeyDataPair({ name: '', data: '', [field]: value });
    }
  };

  React.useEffect(() => {
    getSettings();
  }, []);

  const settingKeysPairs = globalSettings?.find(gs => gs.application === selectedApplication)?.settings;

  const [changedKeys, setChangedKeys] = React.useState<Record<string, string>>({});

  const onChangeSettingKeys = (key: string, value: string) => {
    setChangedKeys({ ...changedKeys, [key]: value });
  };

  const appendedSettings = { ...settingKeysPairs, ...changedKeys };

  const postNewValue = (key: string) => {
    const data = appendedSettings[key];
    const newData = Number(data) && !Number.isNaN(Number(data)) ? Number(data) : data;

    if (newData || newData === 0) {
      putGlobalSettings(selectedApplication, key, newData).then(res => {
        if (res) {
          const changedKeysTemp = changedKeys;
          delete changedKeys[key];
          setChangedKeys(changedKeysTemp);
          getSettings();
        }
      });
    }
  };

  const postNewKeyValue = (key: string, value: any, app?: string) => {
    const newData = Number(value) && !Number.isNaN(Number(value)) ? Number(value) : value;
    if (newData || newData === 0) {
      putGlobalSettings(app || selectedApplication, key, newData).then(res => {
        if (res) {
          setnewApplicationData(null);
          setNewKeyDataPair(null);
          getSettings();
          if (app) selectApplication(app);
        }
      });
    }
  };

  return (
    <>
      <Card className="my-7">
        <CardContent>
          <div className="mb-5 flex">
            <Select value={selectedApplication} onValueChange={selectApplication}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select application" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {availableApplications.map(app => (
                    <SelectItem key={app} value={app}>
                      {app}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            <div className="flex w-full justify-end">
              <DialogComp
                openButton={
                  <Button variant="outline" className="h-9">
                    <PlusIcon strokeWidth={2} />
                    <div>New application</div>
                  </Button>
                }
                title="New application"
                content={
                  <div className="grid gap-y-4 w-4xl min-h-15" style={{ whiteSpace: 'pre', overflow: 'scroll' }}>
                    <div>
                      <Label htmlFor="name">Application name</Label>
                      <Input
                        type="text"
                        id="name"
                        className="w-100"
                        value={newApplicationData?.name || ''}
                        onChange={e => onChangeNewAppData('name', e.target.value)}
                        placeholder="Application name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="newKey">Key name</Label>
                      <Input
                        id="newKey"
                        placeholder="Key name"
                        className="w-100"
                        value={newApplicationData?.key || ''}
                        onChange={e => onChangeNewAppData('key', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="newValue">Value</Label>
                      <Textarea
                        id="newValue"
                        placeholder="Insert value"
                        value={newApplicationData?.value || ''}
                        onChange={e => onChangeNewAppData('value', e.target.value)}
                        style={{ fontSize: '16px', height: '10rem' }}
                      />
                    </div>
                  </div>
                }
                cancel={{ callback: () => '' }}
                accept={{
                  callback: () => {
                    if (newApplicationData?.name && newApplicationData.key && newApplicationData?.value) {
                      postNewKeyValue(
                        newApplicationData?.key,
                        newApplicationData?.value,
                        newApplicationData?.name /** new app name */,
                      );
                      selectApplication('');
                    }
                  },
                  disabled: !newApplicationData?.name || !newApplicationData?.key || !newApplicationData?.value,
                }}
              />
            </div>
          </div>
          {selectedApplication && (
            <div className="flex">
              <div className="flex w-full justify-end">
                <DialogComp
                  openButton={
                    <Button variant="outline" className="h-9">
                      <PlusIcon strokeWidth={2} />
                      <div>New key</div>
                    </Button>
                  }
                  title="New key"
                  content={
                    <div className="grid gap-y-4 w-4xl min-h-15" style={{ whiteSpace: 'pre', overflow: 'scroll' }}>
                      <div>
                        <Label htmlFor="keyname">Key name</Label>
                        <Input
                          type="text"
                          id="keyname"
                          className="w-100"
                          value={newKeyDataPair?.name || ''}
                          onChange={e => onChangeNewKeyDataPair('name', e.target.value)}
                          placeholder="Key name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="data">Data</Label>
                        <Textarea
                          id="data"
                          placeholder="Insert data"
                          value={newKeyDataPair?.data || ''}
                          onChange={e => onChangeNewKeyDataPair('data', e.target.value)}
                          style={{ fontSize: '16px', height: '10rem' }}
                        />
                      </div>
                    </div>
                  }
                  cancel={{ callback: () => '' }}
                  accept={{
                    callback: () => {
                      if (newKeyDataPair?.name && newKeyDataPair.data) {
                        postNewKeyValue(newKeyDataPair.name, newKeyDataPair.data);
                      }
                    },
                    disabled: !newKeyDataPair?.name,
                  }}
                />
              </div>
            </div>
          )}

          <div className="grid gap-y-4">
            {appendedSettings &&
              Object.keys(appendedSettings).map(key => (
                <div key={key} className="flex">
                  <div className="w-[75vw] lg:w-250 xl:w-330">
                    <Label htmlFor={key} className="select-text">
                      {key}
                    </Label>
                    <Textarea
                      id={key}
                      placeholder={key}
                      value={
                        appendedSettings[key] && typeof appendedSettings[key] !== 'string'
                          ? JSON.stringify(appendedSettings[key])
                          : appendedSettings[key]
                            ? appendedSettings[key]
                            : ''
                      }
                      onChange={e => onChangeSettingKeys(key, e.target.value)}
                      style={{ fontSize: '16px', height: '10rem' }}
                    />
                  </div>

                  <div className="w-25 mt-7 my-5 justify-center grid">
                    {Object.keys(changedKeys).find(ck => ck === key) && (
                      <div>
                        <Button className="h-14" onClick={() => postNewValue(key)}>
                          <div>Post</div>
                        </Button>
                        {!tryParseJSONObject(appendedSettings[key]) ? (
                          <ToolTipComponent
                            trigger={
                              <div className="flex w-full justify-center my-5">
                                <AlertCircle />
                              </div>
                            }
                            content={<div>{'Might not be valid JSON, as it has failed to parse.'}</div>}
                          />
                        ) : (
                          <></>
                        )}
                      </div>
                    )}
                    <div className="flex justify-center">
                      <DialogComp
                        openButton={
                          <Button size={'icon'}>
                            <TrashIcon />
                          </Button>
                        }
                        title="Do you really want to delete this key?"
                        cancel={{ callback: () => '' }}
                        accept={{
                          callback: () => {
                            if (selectedApplication) {
                              deleteGlobalSettings(selectedApplication, key).then(res => {
                                if (res) {
                                  getSettings();
                                }
                              });
                            }
                          },
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <AccessRulesList data={{}} />
        </CardContent>
      </Card>
    </>
  );
};

export default GlobalView;
